import { useEffect, useRef, useState } from 'react'
import type { ChatMessage } from '@if/core/types'
import { CURRENT_USER_PARTICIPANT_TYPE } from '@src/data/types/constants'
import { useQueryClient } from '@tanstack/react-query'
import {
  HubConnectionBuilder,
  JsonHubProtocol,
  LogLevel,
  HubConnectionState,
  type HubConnection,
} from '@microsoft/signalr'
import type { ChatRoomLock } from '@if/core/types'
import {
  chatMessageKeysFactory,
  negotiate,
  joinAllMessagesGroup,
  type UnreadEntry,
} from '@src/data/api/chat-message/chat-message-api'

export enum MessageUpdateType {
  Created = 0,
  Deleted = 1,
  MarkedUnread = 2,
  MarkedRead = 3,
  Edited = 4,
}

enum ChatMessageUserType {
  Internal = 0,
  External = 1,
}

export interface MessageUpdatedEvent {
  id: string
  creditApplicationId: string
  merchantId: string
  financingProgramId: string
  updateType: MessageUpdateType
  updatedById?: string
  userMessageType?: number
  content?: string
  sentByName?: string
  sentAt?: string | Date
  readByName?: string
  readAt?: string | Date
  markedUnreadById?: string
}

interface UseChatMessageSignalROptions {
  enabled?: boolean
  onLockChanged?: (lock: ChatRoomLock) => void
  onMessageUpdated?: (event: MessageUpdatedEvent) => void
}

// Module-level singleton to prevent multiple connections across remounts
let globalConnection: HubConnection | null = null
const connectionListeners = new Set<() => void>()

export function useSignalRConnectionReady(): boolean {
  const [ready, setReady] = useState(() => globalConnection?.state === HubConnectionState.Connected)
  useEffect(() => {
    const listener = () => setReady(true)
    connectionListeners.add(listener)
    if (globalConnection?.state === HubConnectionState.Connected) setReady(true)
    return () => { connectionListeners.delete(listener) }
  }, [])
  return ready
}
let globalConnecting = false

export const useChatMessageSignalR = ({ enabled = true, onLockChanged, onMessageUpdated }: UseChatMessageSignalROptions) => {
  const [isConnected, setIsConnected] = useState(false)
  const queryClientRef = useRef(useQueryClient())
  const onLockChangedRef = useRef(onLockChanged)
  onLockChangedRef.current = onLockChanged
  const onMessageUpdatedRef = useRef(onMessageUpdated)
  onMessageUpdatedRef.current = onMessageUpdated

  useEffect(() => {
    if (!enabled) return

    // Already connected or connecting — skip
    if (globalConnection?.state === HubConnectionState.Connected) {
      setIsConnected(true)
      return
    }
    if (globalConnecting) return

    globalConnecting = true

    const connect = async () => {
      try {
        const connectionInfo = await negotiate()

        const connection = new HubConnectionBuilder()
          .withUrl(connectionInfo.url, {
            accessTokenFactory: () => Promise.resolve(connectionInfo.accessToken),
          })
          .withAutomaticReconnect([0, 2000, 10000])
          .withHubProtocol(new JsonHubProtocol())
          .configureLogging(LogLevel.Information)
          .build()

        connection.on('messageUpdated', (event: MessageUpdatedEvent) => {
          onMessageUpdatedRef.current?.(event)

          queryClientRef.current.setQueriesData<UnreadEntry[]>(
            { queryKey: chatMessageKeysFactory.unreadEntries() },
            (oldEntries) => {
              if (!oldEntries) return oldEntries

              const { creditApplicationId, financingProgramId, updateType, userMessageType } = event

              switch (updateType) {
                case MessageUpdateType.Created:
                case MessageUpdateType.MarkedUnread: {
                  if (userMessageType === ChatMessageUserType.Internal) return oldEntries
                  const exists = oldEntries.some((e) => e.creditApplicationId === creditApplicationId)
                  if (!exists) {
                    return [...oldEntries, { creditApplicationId, financingProgramId }]
                  }
                  return oldEntries
                }
                case MessageUpdateType.MarkedRead:
                case MessageUpdateType.Deleted: {
                  return oldEntries.filter((e) => e.creditApplicationId !== creditApplicationId)
                }
                default:
                  return oldEntries
              }
            },
          )
        })

        connection.on('lockChanged', (lock: ChatRoomLock) => {
          onLockChangedRef.current?.(lock)
        })

        connection.onreconnecting(() => {
          setIsConnected(false)
        })

        connection.onreconnected((connId: string | undefined) => {
          setIsConnected(true)
          if (connId) {
            void joinAllMessagesGroup(connId, '', true)
          }
          void queryClientRef.current.invalidateQueries({
            queryKey: chatMessageKeysFactory.unreadEntries(),
          })
        })

        connection.onclose(() => {
          globalConnection = null
          setIsConnected(false)
        })

        await connection.start()

        const connId = connection.connectionId
        if (connId) {
          await joinAllMessagesGroup(connId, '', true)
        }

        globalConnection = connection
        setIsConnected(true)
        connectionListeners.forEach((fn) => fn())
      } catch (err) {
        console.error('ChatMessage SignalR connection error:', err)
        setIsConnected(false)
      } finally {
        globalConnecting = false
      }
    }

    void connect()

    // No cleanup — connection is a singleton that lives for the app lifetime
  }, [enabled])

  return { isConnected }
}

export function getSignalRConnection(): HubConnection | null {
  return globalConnection
}

export function applyChatMessageEvent(messages: ChatMessage[], event: MessageUpdatedEvent): ChatMessage[] {
  if (event.updateType === MessageUpdateType.Created) {
    if (messages.some((m) => m.id === event.id)) return messages

    const newMessage: ChatMessage = {
      id: event.id,
      creditApplicationId: event.creditApplicationId,
      merchantId: event.merchantId,
      content: event.content || '',
      sentByName: event.sentByName || '',
      sentById: event.updatedById || '',
      sentAt: event.sentAt ? (event.sentAt instanceof Date ? event.sentAt : new Date(event.sentAt)) : new Date(),
      isRead: false,
      type: (event.userMessageType ?? CURRENT_USER_PARTICIPANT_TYPE) as ChatMessage['type'],
    }
    return [...messages, newMessage]
  }

  if (event.updateType === MessageUpdateType.Deleted) {
    return messages.filter((m) => m.id !== event.id)
  }

  if (event.updateType === MessageUpdateType.MarkedUnread) {
    return messages.map((m) =>
      m.id === event.id
        ? { ...m, isRead: false, readAt: undefined, readByName: undefined, markedUnreadById: event.updatedById }
        : m,
    )
  }

  if (event.updateType === MessageUpdateType.MarkedRead) {
    return messages.map((m) =>
      m.id === event.id && !m.isRead
        ? {
            ...m,
            isRead: true,
            readAt: event.readAt ? new Date(event.readAt as string) : new Date(),
            readByName: event.readByName,
          }
        : m,
    )
  }

  if (event.updateType === MessageUpdateType.Edited) {
    return messages.map((m) =>
      m.id === event.id ? { ...m, content: event.content || m.content, editedAt: new Date() } : m,
    )
  }

  return messages
}
